﻿import { UdViewStaticDataKey } from './types';

import { Palette } from '@fe/weather';
import {
    AirportCivilOutline,
    AirportHeliportOutline,
    AirportMilitaryCivilOutline,
    AirportMilitaryOutline,
    AirportOtherOutline,
    AirportPrivateOutline,
    AirportSeaplaneOutline,
    FssOutline,
    HoldingPatternOutline,
    NavaidDmeOutline,
    NavaidNdbdmeOutline,
    NavaidNdbOutline,
    NavaidTacanOutline,
    NavaidVordmeOutline,
    NavaidVorOutline,
    NavaidVortacOutline,
    StarHollowOutline,
    TriangleHollowOutline,
    WstationOutline
} from '../../../../../../icons/icons';
import { UDViewStaticDataModel } from '../../../../../../udView/udview';

// Type that holds only keys of UDViewStaticDataModel where the values have a 'show' property of type boolean
export type UDViewStaticDataKeysWithShow = {
    [K in keyof UDViewStaticDataModel]: UDViewStaticDataModel[K] extends { show: boolean } ? K : never;
}[keyof UDViewStaticDataModel];

export const navLayerKeyMap: Partial<Record<UDViewStaticDataKeysWithShow, UdViewStaticDataKey>> = {
    airport: UdViewStaticDataKey.Airport,
    airspace: UdViewStaticDataKey.Airspace,
    airway: UdViewStaticDataKey.Airway,
    drawingObjects: UdViewStaticDataKey.DrawingObjects,
    fixes: UdViewStaticDataKey.Fixes,
    flightServiceStation: UdViewStaticDataKey.FlightServiceStation,
    holding: UdViewStaticDataKey.Holding,
    gridMora: UdViewStaticDataKey.GridMora,
    latLon: UdViewStaticDataKey.LatLon,
    navaids: UdViewStaticDataKey.Navaids,
    sids: UdViewStaticDataKey.Sids,
    stars: UdViewStaticDataKey.Stars,
    sua: UdViewStaticDataKey.Sua,
    timeZone: UdViewStaticDataKey.TimeZone,
    weatherStation: UdViewStaticDataKey.WeatherStation
};

export function getUdViewStaticDataKey(key: UDViewStaticDataKeysWithShow): UdViewStaticDataKey | undefined {
    return navLayerKeyMap[key];
}

export const navLayersPalettes: Palette[] = [
    {
        name: 'Airports',
        id: UdViewStaticDataKey.Airport,
        type: 'categorical',
        entries: [
            {
                label: 'Civil',
                icon: AirportCivilOutline
            },
            {
                label: 'Heliport',
                icon: AirportHeliportOutline
            },
            {
                label: 'Military',
                icon: AirportMilitaryOutline
            },
            {
                label: 'Military-Civil',
                icon: AirportMilitaryCivilOutline
            },
            {
                label: 'Other',
                icon: AirportOtherOutline
            },
            {
                label: 'Private',
                icon: AirportPrivateOutline
            },
            {
                label: 'Seaplane',
                icon: AirportSeaplaneOutline
            }
        ]
    },
    {
        name: 'Airspace',
        id: UdViewStaticDataKey.Airspace,
        type: 'categorical',
        entries: [
            {
                label: 'Class B, C, D',
                color: '#004b94' // todo use config
            },
            {
                label: 'FIR, UIR, Class A, E, F, G',
                color: '#9d5c9c' // todo use config
            }
        ]
    },
    {
        name: 'Airways',
        id: UdViewStaticDataKey.Airway,
        type: 'categorical',
        entries: [
            {
                label: 'RNAV',
                color: '#0000a0' // todo use config
            },
            {
                label: 'VOR, Jet, Military',
                color: '#000000' // todo use config
            }
        ]
    },
    {
        name: 'Holding Patterns',
        id: UdViewStaticDataKey.Holding,
        type: 'categorical',
        entries: [
            {
                label: 'All',
                color: '#6699ffcc', // todo use config
                icon: HoldingPatternOutline
            }
        ]
    },
    {
        name: 'Grid MORA',
        id: UdViewStaticDataKey.GridMora,
        type: 'categorical',
        entries: [
            {
                label: '>= 10000 ft',
                color: '#ff00ff' // todo use config
            },
            {
                label: '< 10000 ft',
                color: '#006400' // todo use config
            }
        ]
    },
    {
        name: 'Special Use Airspace',
        id: UdViewStaticDataKey.Sua,
        type: 'categorical',
        entries: [
            {
                label: 'Prohibited, Restricted, Warning',
                color: '#8597c5' // todo use config
            },
            {
                label: 'Alert, Military Operations Area, Other',
                color: '#b8a189' // todo use config
            }
        ]
    },
    {
        name: 'Fixes',
        id: UdViewStaticDataKey.Fixes,
        type: 'categorical',
        entries: [
            {
                label: 'RNAV',
                icon: StarHollowOutline
            },
            {
                label: 'Other',
                icon: TriangleHollowOutline
            }
        ]
    },
    {
        name: 'Flight Service Stations',
        id: UdViewStaticDataKey.FlightServiceStation,
        type: 'categorical',
        entries: [
            {
                label: 'All',
                icon: FssOutline
            }
        ]
    },
    {
        name: 'Weather Stations',
        id: UdViewStaticDataKey.WeatherStation,
        type: 'categorical',
        entries: [
            {
                label: 'All',
                icon: WstationOutline
            }
        ]
    },
    {
        name: 'NAVAIDs',
        id: UdViewStaticDataKey.Navaids,
        type: 'categorical',
        entries: [
            {
                label: 'NDB',
                icon: NavaidNdbOutline
            },
            {
                label: 'VOR',
                icon: NavaidVorOutline
            },
            {
                label: 'VOR-DME',
                icon: NavaidVordmeOutline
            },
            {
                label: 'VORTAC',
                icon: NavaidVortacOutline
            },
            {
                label: 'TACAN',
                icon: NavaidTacanOutline
            },
            {
                label: 'DME',
                icon: NavaidDmeOutline
            },
            {
                label: 'NDB-DME',
                icon: NavaidNdbdmeOutline
            },
            {
                label: 'VOR',
                icon: NavaidVorOutline
            }
        ]
    }
];

export const terrainShadingPalette: Palette = {
    id: UdViewStaticDataKey.TerrainShading,
    name: 'Terrain shading',
    type: 'gradientBar',
    units: 'ft',
    minValue: 1309,
    maxValue: 25919,
    minDisplayValue: 1309,
    maxDisplayValue: 25919,
    stops: [
        { value: 1309, color: 'rgba(0,0,0,0)', hideLabel: true },
        { value: 1300, color: 'rgb(4,0,108)' },
        { value: 1913, color: 'rgb(5,1,154)', hideLabel: true },
        { value: 2513, color: 'rgb(10,21,189)', hideLabel: true },
        { value: 3114, color: 'rgb(16,44,218)', hideLabel: true },
        { value: 3700, color: 'rgb(24,69,240)' },
        { value: 4314, color: 'rgb(20,112,193)', hideLabel: true },
        { value: 4915, color: 'rgb(39,144,116)', hideLabel: true },
        { value: 5515, color: 'rgb(57,169,29)', hideLabel: true },
        { value: 6100, color: 'rgb(111,186,5)' },
        { value: 6716, color: 'rgb(160,201,4)', hideLabel: true },
        { value: 7316, color: 'rgb(205,216,2)', hideLabel: true },
        { value: 7917, color: 'rgb(244,221,4)', hideLabel: true },
        { value: 8500, color: 'rgb(251,194,14)' },
        { value: 9117, color: 'rgb(252,163,21)', hideLabel: true },
        { value: 9718, color: 'rgb(253,128,20)', hideLabel: true },
        { value: 10318, color: 'rgb(254,85,14)', hideLabel: true },
        { value: 10900, color: 'rgb(243,36,13)' },
        { value: 11519, color: 'rgb(215,5,13)', hideLabel: true },
        { value: 12119, color: 'rgb(225,0,35)', hideLabel: true },
        { value: 12720, color: 'rgb(230,0,55)', hideLabel: true },
        { value: 13300, color: 'rgb(230,0,75)' },
        { value: 13921, color: 'rgb(225,0,95)', hideLabel: true },
        { value: 14521, color: 'rgb(220,0,115)', hideLabel: true },
        { value: 15121, color: 'rgb(210,0,130)', hideLabel: true },
        { value: 15500, color: 'rgb(200,0,145)' },
        { value: 16322, color: 'rgb(190,0,160)', hideLabel: true },
        { value: 16923, color: 'rgb(180,0,175)', hideLabel: true },
        { value: 17523, color: 'rgb(170,0,190)', hideLabel: true },
        { value: 18000, color: 'rgb(160,0,205)' },
        { value: 18724, color: 'rgb(150,0,215)', hideLabel: true },
        { value: 19324, color: 'rgb(140,0,225)', hideLabel: true },
        { value: 19925, color: 'rgb(130,0,235)', hideLabel: true },
        { value: 20500, color: 'rgb(120,0,240)' },
        { value: 21125, color: 'rgb(110,0,245)', hideLabel: true },
        { value: 21726, color: 'rgb(100,0,248)', hideLabel: true },
        { value: 22326, color: 'rgb(90,0,250)', hideLabel: true },
        { value: 23000, color: 'rgb(85,0,252)' },
        { value: 23527, color: 'rgb(80,0,254)', hideLabel: true },
        { value: 24127, color: 'rgb(75,0,255)', hideLabel: true },
        { value: 24728, color: 'rgb(70,0,255)', hideLabel: true },
        { value: 25000, color: 'rgb(65,0,255)' },
        { value: 25900, color: 'rgb(60,0,255)', hideLabel: true }
    ]
};
